import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCampaigns, fetchSubSources } from "../../redux/slices/campaignSlice";
import { fetchLeadPartners } from "../../redux/slices/costPerLeadSlice";
import axios from "axios";
import {
    Button,
    Select,
    SelectItem,
    Input,
    Spinner,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Tooltip,
} from "@heroui/react";
import CustomDateRangePicker from "../../components/Lead_cost_Automation/CustomDateRangePicker";
import { BsPlusCircle, BsTrash, BsChevronDown, BsChevronUp, BsDownload, BsFileEarmarkSpreadsheet } from "react-icons/bs";
import { HiOutlineDocumentReport } from "react-icons/hi";
import { MdDeleteOutline } from "react-icons/md";

const PROXY = process.env.REACT_APP_API_URL + "api/leads";

/* ────────────────────────────────
   OEM MAP — backend expects numeric codes
   ──────────────────────────────── */
// OEM_OPTIONS is populated dynamically inside the component from oemList
const getMappedOemKey = (oemVal, oemList) => {
    if (!oemList || !oemVal) return oemVal;
    if (oemVal === 1 || oemVal === "1") {
        const tvs = oemList.find((o) => o.slug === "tvs-ev" || o.title.toLowerCase().includes("tvs"));
        if (tvs) return tvs._id;
    }
    if (oemVal === 2 || oemVal === "2") {
        const ola = oemList.find((o) => o.slug === "ola-electric-ev" || o.title.toLowerCase().includes("ola"));
        if (ola) return ola._id;
    }
    if (oemVal === 3 || oemVal === "3") {
        const ampere = oemList.find((o) => o.slug === "ampere-ev" || o.title.toLowerCase().includes("ampere"));
        if (ampere) return ampere._id;
    }
    if (oemVal === 4 || oemVal === "4") {
        const revolt = oemList.find((o) => o.slug === "revolt-motors-ev" || o.title.toLowerCase().includes("revolt"));
        if (revolt) return revolt._id;
    }
    if (oemVal === 5 || oemVal === "5") {
        const simple = oemList.find((o) => o.slug === "simple-energy" || o.slug === "simple" || o.title.toLowerCase().includes("simple"));
        if (simple) return simple._id;
    }
    return oemVal;
};

/* ────────────────────────────────
   Report Table Columns
   ──────────────────────────────── */
const reportColumns = [
    { uid: "oem", name: "OEM" },
    { uid: "name", name: "Name" },
    { uid: "type", name: "Type" },
    { uid: "accepted", name: "Accepted" },
    { uid: "rejected", name: "Rejected" },
    { uid: "duplicate", name: "Duplicate" },
    { uid: "unprocessed", name: "Pending" },
    { uid: "total", name: "Total" },
    { uid: "totalSpent", name: "Total Spent" },
    { uid: "cpl", name: "CPL" },
    { uid: "cpAccepted", name: "Cost/Accepted" },
    { uid: "cpUnique", name: "Cost/Unique" },
    { uid: "cpr", name: "CPR" },
    { uid: "cpc", name: "CPC" },
];

const formatCurrency = (val) => {
    if (val == null || isNaN(val)) return "—";
    return "₹" + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(val);
};

const formatNumber = (val) => {
    if (val == null || isNaN(val)) return "—";
    return new Intl.NumberFormat("en-IN").format(val);
};

/* ══════════════════════════════════════════
   SECTION CARD — reusable wrapper
   ══════════════════════════════════════════ */
const SectionCard = ({ title, icon, children, className = "" }) => (
    <div className={`bg-white rounded-2xl shadow-md border border-default-100 overflow-hidden ${className}`}>
        <div className="flex items-center gap-2 px-6 py-4 border-b border-default-100 bg-gradient-to-r from-success/5 to-transparent">
            {icon}
            <h2 className="text-lg font-semibold text-default-800">{title}</h2>
        </div>
        <div className="p-6">{children}</div>
    </div>
);

/* ══════════════════════════════════════════
   SEARCHABLE SELECT — single select with search
   ══════════════════════════════════════════ */
const SearchableSelect = ({ label, placeholder, options, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = options.filter((opt) =>
        opt.toLowerCase().includes(search.toLowerCase())
    );

    const handleSelect = (opt) => {
        onChange(opt);
        setSearch("");
        setIsOpen(false);
    };

    return (
        <div className="flex flex-col gap-1.5 w-full relative" ref={wrapperRef}>
            <label className="text-xs font-medium text-default-700">{label}</label>
            <div
                className={`h-[40px] w-full border ${isOpen ? "border-success ring-1 ring-success" : "border-default-200"
                    } rounded-xl bg-default-100 hover:bg-default-200 transition-colors px-3 flex items-center cursor-text`}
                onClick={() => setIsOpen(true)}
            >
                <input
                    type="text"
                    className="flex-1 bg-transparent outline-none text-sm text-default-700 placeholder:text-default-500"
                    placeholder={value || placeholder}
                    value={isOpen ? search : value || ""}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        if (!isOpen) setIsOpen(true);
                    }}
                    onFocus={() => {
                        setIsOpen(true);
                        setSearch("");
                    }}
                />
                {value && !isOpen && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onChange("");
                        }}
                        className="text-default-400 hover:text-danger text-lg ml-1"
                    >
                        ×
                    </button>
                )}
            </div>

            {isOpen && (
                <div className="absolute top-full left-0 w-full mt-1 max-h-48 overflow-y-auto bg-white border border-default-200 rounded-xl shadow-lg z-50 p-1">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt) => (
                            <div
                                key={opt}
                                onClick={() => handleSelect(opt)}
                                className={`px-3 py-2 text-sm rounded-lg cursor-pointer flex justify-between items-center transition-colors ${opt === value
                                    ? "bg-success/10 text-success font-medium"
                                    : "hover:bg-default-100 text-default-700"
                                    }`}
                            >
                                {opt}
                                {opt === value && <span className="font-bold text-lg">✓</span>}
                            </div>
                        ))
                    ) : (
                        <div className="px-3 py-2 text-sm text-default-500 text-center">No results found</div>
                    )}
                </div>
            )}
        </div>
    );
};

/* ══════════════════════════════════════════
   ROW BUILDER — single row inside OEM block
   ══════════════════════════════════════════ */
const RowBuilder = ({ row, rowIndex, oemIndex, onChange, onRemove, campaignList, subSourceList }) => {
    const handleTypeChange = (keys) => {
        const newType = Array.from(keys)[0] || "subsource";
        onChange(oemIndex, rowIndex, {
            ...row,
            type: newType,
            sub_source: "",
            campaignId: "",
        });
    };

    const handleFieldChange = (field, value) => {
        onChange(oemIndex, rowIndex, { ...row, [field]: value });
    };

    return (
        <div className="relative group border border-default-200 rounded-xl p-4 bg-default-50/50 hover:bg-default-50 transition-colors">
            {/* Row header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Chip size="sm" color="success" variant="flat" className="font-semibold">
                        Row {rowIndex + 1}
                    </Chip>
                    <Chip size="sm" variant="dot" color={row.type === "campaign" ? "secondary" : "primary"}>
                        {row.type === "campaign" ? "Campaign" : "Subsource"}
                    </Chip>
                </div>
                <Tooltip content="Remove row" color="danger">
                    <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        color="danger"
                        onPress={() => onRemove(oemIndex, rowIndex)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                        <MdDeleteOutline size={18} />
                    </Button>
                </Tooltip>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Type selector */}
                <Select
                    label="Row Type"
                    labelPlacement="outside"
                    placeholder="Select type"
                    selectedKeys={row.type ? [row.type] : ["subsource"]}
                    onSelectionChange={handleTypeChange}
                    color="success"
                    variant="bordered"
                    size="md"
                >
                    <SelectItem key="subsource" value="subsource">Subsource</SelectItem>
                    <SelectItem key="campaign" value="campaign">Campaign</SelectItem>
                </Select>

                {/* Subsource / Campaign selector */}
                {row.type === "subsource" ? (
                    <SearchableSelect
                        label="Subsource"
                        placeholder="Search subsource..."
                        options={subSourceList}
                        value={row.sub_source}
                        onChange={(val) => handleFieldChange("sub_source", val)}
                    />
                ) : (
                    <Select
                        label="Campaign"
                        labelPlacement="outside"
                        placeholder="Select campaign"
                        selectedKeys={row.campaignId ? [row.campaignId] : []}
                        onSelectionChange={(keys) => handleFieldChange("campaignId", Array.from(keys)[0] || "")}
                        color="success"
                        variant="bordered"
                        size="md"
                    >
                        {campaignList.map((c) => (
                            <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>
                        ))}
                    </Select>
                )}

                {/* Total Spent */}
                <Input
                    label="Total Spent"
                    labelPlacement="outside"
                    placeholder="0"
                    type="number"
                    step="any"
                    value={row.total_spent?.toString() || ""}
                    onChange={(e) => handleFieldChange("total_spent", parseFloat(e.target.value) || 0)}
                    color="success"
                    variant="bordered"
                    size="md"
                    startContent={<span className="text-default-400 text-xs">₹</span>}
                />

                {/* CPR */}
                <Input
                    label="CPR"
                    labelPlacement="outside"
                    placeholder="0"
                    type="number"
                    step="any"
                    value={row.cpr?.toString() || ""}
                    onChange={(e) => handleFieldChange("cpr", parseFloat(e.target.value) || 0)}
                    color="success"
                    variant="bordered"
                    size="md"
                    startContent={<span className="text-default-400 text-xs">₹</span>}
                />

                {/* CPC */}
                <Input
                    label="CPC"
                    labelPlacement="outside"
                    placeholder="0"
                    type="number"
                    step="any"
                    value={row.cpc?.toString() || ""}
                    onChange={(e) => handleFieldChange("cpc", parseFloat(e.target.value) || 0)}
                    color="success"
                    variant="bordered"
                    size="md"
                    startContent={<span className="text-default-400 text-xs">₹</span>}
                />
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════
   OEM BLOCK — single OEM with its rows
   ══════════════════════════════════════════ */
const OemBlock = ({
    oemBlock,
    oemIndex,
    onOemChange,
    onRowChange,
    onAddRow,
    onRemoveRow,
    onRemoveOem,
    campaignList,
    subSourceList,
    OEM_OPTIONS = [],
    oemList = [],
}) => {
    const [collapsed, setCollapsed] = useState(false);
    const [oemCampaigns, setOemCampaigns] = useState([]);

    const selectedOemLabel = OEM_OPTIONS.find((o) => o.key === oemBlock.oem)?.label || "Unselected";

    useEffect(() => {
        if (oemList && oemList.length > 0 && oemBlock.oem) {
            const oemDbId = getMappedOemKey(oemBlock.oem, oemList);
            if (oemDbId && /^[0-9a-fA-F]{24}$/.test(oemDbId)) {
                axios.get(`${process.env.REACT_APP_API_URL}api/campaign/fetch-campaigns`, {
                    params: { page: 1, limit: 1000, oem: oemDbId }
                })
                .then((res) => {
                    if (res.data?.result?.docs) {
                        setOemCampaigns(res.data.result.docs);
                    }
                })
                .catch((err) => console.error("Error fetching campaigns for OEM:", err));
            } else {
                setOemCampaigns([]);
            }
        } else {
            setOemCampaigns([]);
        }
    }, [oemBlock.oem, oemList]);

    return (
        <div className="border-2 border-success/20 rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
            {/* OEM Header */}
            <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-success/10 via-success/5 to-transparent border-b border-success/10">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setCollapsed(!collapsed)}
                        className="p-1 hover:bg-success/10 rounded-lg transition-colors"
                    >
                        {collapsed ? <BsChevronDown size={16} className="text-success" /> : <BsChevronUp size={16} className="text-success" />}
                    </button>
                    <Chip color="success" className="text-white" variant="solid" size="sm">
                        OEM {oemIndex + 1}
                    </Chip>
                    <span className="text-sm font-bold text-default-600">{selectedOemLabel}</span>
                    <Chip size="sm" variant="flat" color="default">
                        {oemBlock.rows.length} row{oemBlock.rows.length !== 1 ? "s" : ""}
                    </Chip>
                </div>
                <Tooltip content="Remove OEM block" color="danger">
                    <Button isIconOnly size="sm" variant="flat" color="danger" onPress={() => onRemoveOem(oemIndex)}>
                        <BsTrash size={14} />
                    </Button>
                </Tooltip>
            </div>

            {!collapsed && (
                <div className="p-5 flex flex-col gap-5">
                    {/* OEM Selector */}
                    <Select
                        label="Select OEM"
                        labelPlacement="outside"
                        placeholder="Choose OEM"
                        selectedKeys={oemBlock.oem ? [String(oemBlock.oem)] : []}
                        onSelectionChange={(keys) => {
                            const val = Array.from(keys)[0];
                            onOemChange(oemIndex, val ? parseInt(val) : "");
                        }}
                        color="success"
                        variant="bordered"
                        className="max-w-xs"
                    >
                        {OEM_OPTIONS.map((o) => (
                            <SelectItem key={String(o.key)} value={String(o.key)}>{o.label}</SelectItem>
                        ))}
                    </Select>

                    {/* Rows */}
                    <div className="flex flex-col gap-3">
                        {oemBlock.rows.length === 0 && (
                            <div className="text-center py-8 text-default-400 text-sm border-2 border-dashed border-default-200 rounded-xl">
                                No rows yet. Click "Add Row" to begin.
                            </div>
                        )}
                        {oemBlock.rows.map((row, rIdx) => (
                            <RowBuilder
                                key={rIdx}
                                row={row}
                                rowIndex={rIdx}
                                oemIndex={oemIndex}
                                onChange={onRowChange}
                                onRemove={onRemoveRow}
                                campaignList={oemCampaigns}
                                subSourceList={subSourceList}
                            />
                        ))}
                    </div>

                    {/* Add Row Button */}
                    <Button
                        variant="flat"
                        color="success"
                        size="sm"
                        startContent={<BsPlusCircle size={14} />}
                        onPress={() => onAddRow(oemIndex)}
                        className="self-start"
                    >
                        Add Row
                    </Button>
                </div>
            )}
        </div>
    );
};

/* ══════════════════════════════════════════════════
   MAIN PAGE — GenerateReport
   ══════════════════════════════════════════════════ */
const GenerateReport = () => {
    const dispatch = useDispatch();
    const { campaigns, subSources: subSourceList } = useSelector((state) => state.campaigns);
    const { leadPartners: oemList } = useSelector((state) => state.leadPartners);
    console.log("🚀 ~ :411 ~ GenerateReport ~ oemList:", oemList)

    const OEM_OPTIONS = useMemo(() => {
        const seen = new Set();
        const options = [];
        for (const o of (oemList || [])) {
            let key = o._id;
            if (o.slug === "tvs-ev") key = 1;
            else if (o.slug === "ola-electric-ev") key = 2;
            else if (o.slug === "ampere-ev") key = 3;
            else if (o.slug === "revolt-motors-ev") key = 4;
            else if (o.slug === "simple-energy" || o.slug === "simple" || o.title?.toLowerCase().includes("simple")) key = 5;

            if (!seen.has(key)) {
                seen.add(key);
                options.push({
                    key,
                    label: o.title
                });
            }
        }
        return options.sort((a, b) => {
            if (typeof a.key === 'number' && typeof b.key === 'number') return a.key - b.key;
            return 0;
        });
    }, [oemList]);

    // ── LocalStorage keys ──
    const LS_KEY_STARTDATE = 'generateReport_startDate';
    const LS_KEY_ENDDATE = 'generateReport_endDate';
    const LS_KEY_OEMBLOCKS = 'generateReport_oemBlocks';

    // ── Date state — both startDate and endDate are picked via range picker ──
    const [startDate, setStartDate] = useState(() => {
        try { return localStorage.getItem(LS_KEY_STARTDATE) || ""; } catch { return ""; }
    });
    const [endDate, setEndDate] = useState(() => {
        try { return localStorage.getItem(LS_KEY_ENDDATE) || ""; } catch { return ""; }
    });

    // ── OEM Blocks ──
    const [oemBlocks, setOemBlocks] = useState(() => {
        try {
            const saved = localStorage.getItem(LS_KEY_OEMBLOCKS);
            return saved ? JSON.parse(saved) : [];
        } catch { return []; }
    });

    // ── Persist to localStorage on change ──
    useEffect(() => {
        try { localStorage.setItem(LS_KEY_STARTDATE, startDate); } catch { }
    }, [startDate]);

    useEffect(() => {
        try { localStorage.setItem(LS_KEY_ENDDATE, endDate); } catch { }
    }, [endDate]);

    useEffect(() => {
        try { localStorage.setItem(LS_KEY_OEMBLOCKS, JSON.stringify(oemBlocks)); } catch { }
    }, [oemBlocks]);

    // ── Clear all saved data ──
    const clearAll = useCallback(() => {
        setStartDate("");
        setEndDate("");
        setOemBlocks([]);
        setReportData(null);
        setReportError("");
        setExportUrl("");
        try {
            localStorage.removeItem(LS_KEY_STARTDATE);
            localStorage.removeItem(LS_KEY_ENDDATE);
            localStorage.removeItem(LS_KEY_OEMBLOCKS);
        } catch { }
    }, []);

    // ── Report State ──
    const [reportData, setReportData] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [reportError, setReportError] = useState("");
    const [exportUrl, setExportUrl] = useState("");

    // ── Fetch initial data ──
    useEffect(() => {
        dispatch(fetchLeadPartners());
        dispatch(fetchSubSources());
        // Fetch ALL campaigns (large limit) for the dropdown
        dispatch(fetchCampaigns({ page: 1, limit: 1000, search: "" }));
    }, [dispatch]);

    /* ── OEM Block Handlers ── */
    const addOemBlock = () => {
        setOemBlocks((prev) => [...prev, { oem: "", rows: [] }]);
    };

    const removeOemBlock = (oemIndex) => {
        setOemBlocks((prev) => prev.filter((_, i) => i !== oemIndex));
    };

    const handleOemChange = (oemIndex, value) => {
        setOemBlocks((prev) => prev.map((block, i) => i === oemIndex ? { ...block, oem: value } : block));
    };

    /* ── Row Handlers ── */
    const addRow = (oemIndex) => {
        setOemBlocks((prev) =>
            prev.map((block, i) =>
                i === oemIndex
                    ? { ...block, rows: [...block.rows, { type: "subsource", sub_source: "", campaignId: "", total_spent: 0, cpr: 0, cpc: 0 }] }
                    : block
            )
        );
    };

    const removeRow = (oemIndex, rowIndex) => {
        setOemBlocks((prev) =>
            prev.map((block, i) =>
                i === oemIndex
                    ? { ...block, rows: block.rows.filter((_, ri) => ri !== rowIndex) }
                    : block
            )
        );
    };

    const handleRowChange = (oemIndex, rowIndex, updatedRow) => {
        setOemBlocks((prev) =>
            prev.map((block, i) =>
                i === oemIndex
                    ? { ...block, rows: block.rows.map((r, ri) => (ri === rowIndex ? updatedRow : r)) }
                    : block
            )
        );
    };

    const { user } = useSelector((state) => state.auth);

    /* ── Build Payload ── */
    const payload = useMemo(() => {
        let formattedStart = "";
        if (startDate) {
            const sUTC = new Date(startDate);
            const start = new Date(
                sUTC.getUTCFullYear(),
                sUTC.getUTCMonth(),
                sUTC.getUTCDate(),
                sUTC.getUTCHours(),
                sUTC.getUTCMinutes(),
                0, 0
            );
            formattedStart = start.toISOString();
        }

        let formattedEnd = "";
        if (endDate) {
            const eUTC = new Date(endDate);
            const end = new Date(
                eUTC.getUTCFullYear(),
                eUTC.getUTCMonth(),
                eUTC.getUTCDate(),
                eUTC.getUTCHours(),
                eUTC.getUTCMinutes(),
                59, 999
            );
            formattedEnd = end.toISOString();
        }

        return {
            startDate: formattedStart,
            endDate: formattedEnd,
            oems: oemBlocks.map((block) => ({
                oem: block.oem,
                rows: block.rows.map((row) => {
                    const base = {
                        type: row.type,
                        total_spent: row.total_spent || 0,
                        cpr: row.cpr || 0,
                        cpc: row.cpc || 0,
                    };
                    if (row.type === "campaign") {
                        return { ...base, campaignId: row.campaignId };
                    } else {
                        return { ...base, sub_source: row.sub_source };
                    }
                }),
            })),
            // email: "priyansh.ahura@gmail.com",
            email: user.email,
        };
    }, [startDate, endDate, oemBlocks]);

    /* ── Generate Report ── */
    const handleGenerateReport = useCallback(async () => {
        setReportLoading(true);
        setReportError("");
        setReportData(null);
        setExportUrl("");

        try {
            const res = await axios.post(`${PROXY}/generate-lead-cost-report`, payload);

            if (res.data?.result) {
                setExportUrl(res.data.result);
            }

            // The backend pushes to a queue and returns the download URL.
            // We store the payload-based preview since the backend sends file async.
            // Show a success message with the download link.  
            setReportData(res.data);
        } catch (err) {
            setReportError(err?.response?.data?.message || err?.message || "Something went wrong");
        } finally {
            setReportLoading(false);
        }
    }, [payload]);

    /* ── Validation ── */
    const isPayloadValid = useMemo(() => {
        if (!startDate || !endDate) return false;
        if (oemBlocks.length === 0) return false;
        for (const block of oemBlocks) {
            if (!block.oem) return false;
            if (block.rows.length === 0) return false;
            for (const row of block.rows) {
                if (row.type === "subsource" && !row.sub_source) return false;
                if (row.type === "campaign" && !row.campaignId) return false;
            }
        }
        return true;
    }, [startDate, endDate, oemBlocks]);

    /* ── Flatten report data for table ── */
    const flatReportRows = useMemo(() => {
        if (!reportData?.result) return [];
        // If the backend returns finalReport in the response directly
        // For now we show the payload structure as a confirmation
        return [];
    }, [reportData]);

    return (
        <div className="w-full h-full overflow-y-auto default-scrollbar">
            <div className="max-w-7xl mx-auto p-6 flex flex-col gap-6">

                {/* ═══════════════════════════════════
                    PAGE HEADER
                   ═══════════════════════════════════ */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-default-800 flex items-center gap-2">
                            <HiOutlineDocumentReport className="text-success" size={28} />
                            Generate Lead Cost Report
                        </h1>
                        <p className="text-sm text-default-500 mt-1">
                            Select a date & time, add OEM blocks with rows, and generate your report.
                        </p>
                    </div>
                </div>

                {/* ═══════════════════════════════════
                    SECTION 1 — DATE & TIME
                   ═══════════════════════════════════ */}
                <SectionCard
                    title="Select Date Range & Time"
                    icon={<span className="text-success text-lg">📅</span>}
                >
                    <div className="flex flex-col gap-4">
                        <p className="text-xs text-default-500">
                            Pick a <strong>start date</strong> and <strong>end date</strong> range. Use the <strong>Full Day</strong> button for today's full range.
                        </p>
                        <CustomDateRangePicker
                            onRangeChange={({ startDate: s, endDate: e }) => {
                                setStartDate(s);
                                setEndDate(e);
                            }}
                        />
                        {startDate && endDate && (
                            <div className="flex items-center gap-3 flex-wrap">
                                <Chip size="sm" variant="flat" color="success">
                                    Start: {new Date(startDate).toLocaleString("en-IN", { timeZone: "UTC" })}
                                </Chip>
                                <span className="text-default-400">→</span>
                                <Chip size="sm" variant="flat" color="success">
                                    End: {new Date(endDate).toLocaleString("en-IN", { timeZone: "UTC" })}
                                </Chip>
                            </div>
                        )}
                    </div>
                </SectionCard>

                {/* ═══════════════════════════════════
                    SECTION 2 & 3 — OEM BLOCK BUILDER + ROW BUILDER
                   ═══════════════════════════════════ */}
                <SectionCard
                    title="OEM Blocks & Rows"
                    icon={<span className="text-success text-lg">🏢</span>}
                >
                    {/* Clear All button */}
                    <div className="flex justify-end -mt-2 mb-3">
                        <Button
                            variant="flat"
                            color="danger"
                            size="sm"
                            startContent={<BsTrash size={14} />}
                            onPress={clearAll}
                        >
                            Clear All
                        </Button>
                    </div>
                    <div className="flex flex-col gap-4">
                        {oemBlocks.length === 0 && (
                            <div className="text-center py-12 text-default-400 border-2 border-dashed border-default-200 rounded-xl">
                                <p className="text-lg mb-2">No OEM blocks added yet</p>
                                <p className="text-sm">Click the button below to start building your report payload.</p>
                            </div>
                        )}

                        {oemBlocks.map((block, oIdx) => (
                            <OemBlock
                                key={oIdx}
                                oemBlock={block}
                                oemIndex={oIdx}
                                onOemChange={handleOemChange}
                                onRowChange={handleRowChange}
                                onAddRow={addRow}
                                onRemoveRow={removeRow}
                                onRemoveOem={removeOemBlock}
                                campaignList={campaigns || []}
                                subSourceList={subSourceList || []}
                                OEM_OPTIONS={OEM_OPTIONS}
                                oemList={oemList || []}
                            />
                        ))}

                        {/* SECTION 4 — Add OEM Block */}
                        <Button
                            variant="bordered"
                            color="success"
                            startContent={<BsPlusCircle size={16} />}
                            onPress={addOemBlock}
                            className="self-start font-semibold"
                        >
                            Add OEM Block
                        </Button>
                    </div>
                </SectionCard>



                {/* ═══════════════════════════════════
                    SECTION 6 — ACTION BUTTONS
                   ═══════════════════════════════════ */}
                <div className="flex flex-wrap items-center gap-4">
                    <Button
                        color="success"
                        className="text-white font-semibold px-8 shadow-lg shadow-success/20"
                        size="lg"
                        startContent={reportLoading ? null : <HiOutlineDocumentReport size={20} />}
                        onPress={handleGenerateReport}
                        isLoading={reportLoading}
                        isDisabled={!isPayloadValid || reportLoading}
                    >
                        Generate Report
                    </Button>

                    {!isPayloadValid && (
                        <p className="text-sm text-warning-500 font-medium">
                            ⚠ Please fill in all required fields before generating.
                        </p>
                    )}
                </div>

                {/* ═══════════════════════════════════
                    Error / Success Messages
                   ═══════════════════════════════════ */}
                {reportError && (
                    <div className="bg-danger-50 border border-danger-200 text-danger-700 px-5 py-3 rounded-xl text-sm font-medium">
                        ❌ {reportError}
                    </div>
                )}

                {reportData && !reportError && (
                    <div className="bg-success-50 border border-success-200 text-success-700 px-5 py-3 rounded-xl text-sm font-medium flex items-center gap-3">
                        <BsFileEarmarkSpreadsheet size={20} />
                        <div>
                            <p className="font-semibold">{reportData.message || "Report generated successfully!"}</p>
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════
                    SECTION 7 — OPTIONAL REPORT TABLE PREVIEW
                   ═══════════════════════════════════ */}
                {reportData && reportData.finalReport && reportData.finalReport.length > 0 && (
                    <SectionCard
                        title="Report Preview"
                        icon={<span className="text-success text-lg">📊</span>}
                    >
                        <div className="overflow-x-auto rounded-xl border border-default-200">
                            <Table
                                aria-label="Lead Cost Report Preview"
                                removeWrapper
                                classNames={{
                                    th: "bg-success/10 text-default-700 font-semibold text-xs border-none",
                                    td: "border-none !py-3 text-sm",
                                    tr: "border-b border-default-100 last:border-none hover:bg-default-50 transition-colors",
                                }}
                            >
                                <TableHeader columns={reportColumns}>
                                    {(column) => (
                                        <TableColumn key={column.uid} align={column.uid === "oem" ? "start" : "end"}>
                                            {column.name}
                                        </TableColumn>
                                    )}
                                </TableHeader>
                                <TableBody>
                                    {reportData.finalReport.flatMap((oemData, oemIdx) =>
                                        oemData.rows.map((row, rIdx) => (
                                            <TableRow key={`${oemIdx}-${rIdx}`}>
                                                <TableCell>
                                                    <Chip size="sm" color="success" variant="flat">
                                                        {OEM_OPTIONS.find((o) => o.key === oemData.oem)?.label || oemData.oem}
                                                    </Chip>
                                                </TableCell>
                                                <TableCell className="font-medium">{row.name}</TableCell>
                                                <TableCell>
                                                    <Chip size="sm" variant="dot" color={row.type === "campaign" ? "secondary" : "primary"}>
                                                        {row.type}
                                                    </Chip>
                                                </TableCell>
                                                <TableCell className="text-success font-semibold">{formatNumber(row.accepted)}</TableCell>
                                                <TableCell className="text-danger">{formatNumber(row.rejected)}</TableCell>
                                                <TableCell className="text-warning">{formatNumber(row.duplicate)}</TableCell>
                                                <TableCell>{formatNumber(row.unprocessed)}</TableCell>
                                                <TableCell className="font-bold">{formatNumber(row.total)}</TableCell>
                                                <TableCell>{formatCurrency(row.totalSpent)}</TableCell>
                                                <TableCell>{formatCurrency(row.cpl)}</TableCell>
                                                <TableCell>{formatCurrency(row.cpAccepted)}</TableCell>
                                                <TableCell>{formatCurrency(row.cpUnique)}</TableCell>
                                                <TableCell>{formatCurrency(row.cpr)}</TableCell>
                                                <TableCell>{formatCurrency(row.cpc)}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>

                        {/* OEM Totals */}
                        {reportData.finalReport.map((oemData, oemIdx) => (
                            <div key={oemIdx} className="mt-4 p-4 bg-default-50 rounded-xl border border-default-100">
                                <p className="text-sm font-semibold text-default-700 mb-2">
                                    {OEM_OPTIONS.find((o) => o.key === oemData.oem)?.label || oemData.oem} — Totals
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                                    {[
                                        { label: "Total Leads", value: formatNumber(oemData.totals?.totalLeads) },
                                        { label: "Accepted", value: formatNumber(oemData.totals?.accepted), color: "text-success" },
                                        { label: "Rejected", value: formatNumber(oemData.totals?.rejected), color: "text-danger" },
                                        { label: "Pending", value: formatNumber(oemData.totals?.unprocessed) },
                                        { label: "Duplicate", value: formatNumber(oemData.totals?.duplicate), color: "text-warning" },
                                        { label: "Total Spent", value: formatCurrency(oemData.totals?.totalSpent) },
                                    ].map((item) => (
                                        <div key={item.label} className="bg-white rounded-lg p-3 border border-default-100 text-center">
                                            <p className="text-xs text-default-500">{item.label}</p>
                                            <p className={`text-lg font-bold ${item.color || "text-default-800"}`}>{item.value}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </SectionCard>
                )}

                {/* Bottom spacer */}
                <div className="h-8" />
            </div>
        </div>
    );
};

export default GenerateReport;